'use client';

import React, { useState } from 'react';
import { BiMinus, BiPlus, BiSearch } from 'react-icons/bi';
import { RiQuestionAnswerLine } from 'react-icons/ri';
import { HiOutlineChatBubbleLeftRight, HiOutlineQuestionMarkCircle } from 'react-icons/hi2';
import faqs from '../../faq.json';
import { useTranslation } from '@/hooks/useTranslation';
import LouiseButton from '@/components/buttons/LouiseButton';
import ReactHtmlParser from 'react-html-parser';

interface IData {
  title: string;
  questions: Array<{ q: string; text: string; section?: string }>;
}

type Props = {
  session:
    | 'VideoChatbotFAQs'
    | 'SiteFAQs'
    | 'TextBotFAQs'
    | 'AIChatbotFAQs'
    | 'HumanCloneFAQs'
    | 'AgenciesFAQs'
    | 'AffiliateFAQs'
    | 'SiteBotFAQs'
    | 'CallBotFAQs'
    | 'VoicemailFAQs'
    | 'ALL';
  locale: string;
};

const SiteFrequentlyAskedQuestionsComponent = ({ session, locale }: Props) => {
  const { t } = useTranslation(locale);

  let data: IData;

  if (session === 'ALL') {
    const mergedQuestions: IData['questions'] = [];
    Object.keys(faqs).forEach((section) => {
      const sectionData = faqs[section][locale.slice(0, 2)];
      if (sectionData?.questions) {
        // Add section information to each question when merging
        const questionsWithSection = sectionData.questions.map((q) => ({
          ...q,
          section: section,
        }));
        mergedQuestions.push(...questionsWithSection);
      }
    });
    data = {
      title: 'All FAQs',
      questions: mergedQuestions,
    };
  } else {
    data = faqs[session][locale.slice(0, 2)] ?? { title: '', questions: [] };
  }

  const [openItems, setOpenItems] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const getBadgeInfo = (sectionKey: string) => {
    const badges = {
      VideoChatbotFAQs: {
        label: 'VideoBot',
        color: 'tw-bg-purple-100 tw-text-purple-700',
      },
      SiteFAQs: {
        label: 'General',
        color: 'tw-bg-gray-100 tw-text-gray-700',
      },
      TextBotFAQs: {
        label: 'TextBot',
        color: 'tw-bg-green-100 tw-text-green-700',
      },
      AIChatbotFAQs: {
        label: 'QuickBot/DocBot',
        color: 'tw-bg-blue-100 tw-text-blue-700',
      },
      HumanCloneFAQs: {
        label: 'Human Clone',
        color: 'tw-bg-orange-100 tw-text-orange-700',
      },
      AgenciesFAQs: {
        label: 'Agencies',
        color: 'tw-bg-indigo-100 tw-text-indigo-700',
      },
      AffiliateFAQs: {
        label: 'Business Partner',
        color: 'tw-bg-teal-100 tw-text-teal-700',
      },
      SiteBotFAQs: {
        label: 'SiteBot',
        color: 'tw-bg-purple-100 tw-text-purple-700',
      },
      CallBotFAQs: {
        label: 'CallBot',
        color: 'tw-bg-yellow-100 tw-text-yellow-700',
      },
      VoicemailFAQs: {
        label: 'TextBot',
        color: 'tw-bg-green-100 tw-text-green-700',
      },
    };

    return (
      badges[sectionKey] || {
        label: 'General',
        color: 'tw-bg-gray-100 tw-text-gray-700',
      }
    );
  };

  if (!data || data.questions?.length < 1) {
    return null;
  }

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  // Filter questions based on search term
  const filteredQuestions =
    data?.questions.filter(
      (item) =>
        item.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.text.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  return (
    <section className="tw-py-10 lg:tw-py-10 tw-relative tw-overflow-hidden tw-font-['Lexend'] tw-bg-transparent">
      {/* Background decorations */}
      <div className="tw-absolute tw-inset-0">
        <div className="tw-absolute tw-top-1/4 tw-left-1/4 tw-w-72 tw-h-72 tw-bg-blue-100/20 tw-rounded-full tw-blur-3xl"></div>
        <div className="tw-absolute tw-bottom-1/4 tw-right-1/4 tw-w-80 tw-h-80 tw-bg-purple-100/20 tw-rounded-full tw-blur-3xl"></div>
      </div>

      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl tw-relative tw-z-10">
        {/* Header */}
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <HiOutlineQuestionMarkCircle className="tw-w-4 tw-h-4" />
            {t('faq/badge')}
          </div>

          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-leading-tight">
            {data.title}
          </h2>

          <p className="tw-text-base tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-leading-relaxed tw-mb-8">
            {t('faq/subtitle')}
          </p>

          {/* Search Bar */}
          <div className="tw-relative tw-max-w-md tw-mx-auto">
            <div className="tw-absolute tw-inset-y-0 tw-left-0 tw-pl-4 tw-flex tw-items-center tw-pointer-events-none">
              <BiSearch className="tw-h-5 tw-w-5 tw-text-gray-400" />
            </div>
            <input
              type="text"
              placeholder={t('faq/search-placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="tw-w-full tw-pl-12 tw-pr-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-xl tw-bg-white tw-text-gray-900 tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-transparent tw-transition-all tw-duration-300"
            />
          </div>
        </div>

        {/* FAQ Items */}
        <div className="tw-space-y-4 tw-mb-16">
          {filteredQuestions.length === 0 && searchTerm ? (
            <div className="tw-text-center tw-py-12">
              <div className="tw-w-16 tw-h-16 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                <BiSearch className="tw-w-8 tw-h-8 tw-text-gray-400" />
              </div>
              <h3 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">{t('faq/no-results-title')}</h3>
              <p className="tw-text-gray-600">{t('faq/no-results-description')}</p>
            </div>
          ) : (
            filteredQuestions.map((item, index) => {
              const isOpen = openItems.has(index);
              // Only show badge when session is "ALL" and question has section info
              const shouldShowBadge = session === 'ALL' && item.section;
              const badgeInfo = shouldShowBadge ? getBadgeInfo(item.section as string) : null;

              return (
                <div
                  key={index}
                  className="tw-group tw-bg-white tw-rounded-2xl tw-border tw-border-gray-200 tw-shadow-sm tw-transition-all tw-duration-300 hover:tw-shadow-lg hover:tw-border-blue-200 tw-overflow-hidden"
                >
                  {/* Question Header */}
                  <button
                    onClick={() => toggleItem(index)}
                    className="tw-w-full tw-p-6 lg:tw-p-8 tw-text-left tw-flex tw-items-center tw-justify-between tw-gap-4 tw-transition-colors tw-duration-300 hover:tw-bg-gray-50"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <div className="tw-flex tw-items-start tw-gap-4 tw-flex-grow tw-min-w-0">
                      <div
                        className={`tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 ${
                          isOpen
                            ? 'tw-bg-blue-600 tw-text-white'
                            : 'tw-bg-blue-100 tw-text-blue-600 group-hover:tw-bg-blue-200'
                        }`}
                      >
                        <RiQuestionAnswerLine className="tw-w-5 tw-h-5" />
                      </div>

                      <div className="tw-flex-grow tw-min-w-0">
                        {/* Section Badge - Only show when session is "ALL" */}
                        {shouldShowBadge && badgeInfo && (
                          <div className="tw-mb-2">
                            <span
                              className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${badgeInfo.color}`}
                            >
                              {badgeInfo.label}
                            </span>
                          </div>
                        )}

                        {/* Question Title */}
                        <h3 className="tw-text-lg lg:tw-text-xl tw-font-bold tw-text-gray-900 tw-leading-tight tw-text-left">
                          {item.q}
                        </h3>
                      </div>
                    </div>

                    <div
                      className={`tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 ${
                        isOpen
                          ? 'tw-bg-blue-100 tw-text-blue-600 tw-rotate-180'
                          : 'tw-bg-gray-100 tw-text-gray-500 group-hover:tw-bg-gray-200'
                      }`}
                    >
                      {isOpen ? <BiMinus className="tw-w-5 tw-h-5" /> : <BiPlus className="tw-w-5 tw-h-5" />}
                    </div>
                  </button>

                  {/* Answer Content */}
                  <div
                    id={`faq-answer-${index}`}
                    className={`tw-transition-all tw-duration-500 tw-ease-in-out ${
                      isOpen ? 'tw-opacity-100' : 'tw-max-h-0 tw-opacity-0'
                    } tw-overflow-hidden`}
                    style={{
                      maxHeight: isOpen ? 'none' : '0',
                      transition: 'max-height 0.5s ease-in-out, opacity 0.5s ease-in-out',
                    }}
                  >
                    <div className="tw-px-6 lg:tw-px-8 tw-pb-6 lg:tw-pb-8">
                      <div className="tw-pl-14">
                        <div className="tw-w-full tw-h-px tw-bg-gray-200 tw-mb-6"></div>
                        <div className="tw-text-gray-600 tw-leading-relaxed tw-text-base lg:tw-text-lg">
                          {ReactHtmlParser(item.text)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Quick Stats */}
        <div className="tw-grid grid-cols-2 md:tw-grid-cols-2 tw-gap-6 tw-mb-16">
          <div className="tw-text-center tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-100 tw-col-span-2 md:tw-col-span-1">
            <div className="tw-text-2xl tw-font-bold tw-text-green-600 tw-mb-2">24/7</div>
            <div className="tw-text-sm tw-text-gray-600">{t('faq/support-availability')}</div>
          </div>
          <div className="tw-text-center tw-p-6 tw-bg-white tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-100 tw-col-span-2 md:tw-col-span-1">
            <div className="tw-text-2xl tw-font-bold tw-text-purple-600 tw-mb-2">9h</div>
            <div className="tw-text-sm tw-text-gray-600">{t('faq/avg-response')}</div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="tw-text-center">
          <div className="tw-bg-gradient-to-r tw-from-blue-50 tw-to-purple-50 tw-rounded-3xl tw-p-8 lg:tw-p-12 tw-border tw-border-blue-100">
            <div className="tw-w-16 tw-h-16 tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-rounded-2xl tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-6">
              <HiOutlineChatBubbleLeftRight className="tw-w-8 tw-h-8 tw-text-white" />
            </div>

            <h3 className="tw-text-2xl lg:tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-4">{t('faq/cta/title')}</h3>
            <p className="tw-text-lg tw-text-gray-600 tw-mb-8 tw-max-w-2xl tw-mx-auto">{t('faq/cta/description')}</p>

            <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center">
              <LouiseButton
                color="gray-light"
                cta={t('faq/cta/primary')}
                ctaTarget="contact"
                locale={locale}
                icon={<HiOutlineChatBubbleLeftRight className="tw-w-5 tw-h-5" />}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SiteFrequentlyAskedQuestionsComponent;
